/**
 * features/quotations/hooks.ts
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QuotationInput, QuotationStatus } from '@ft/core';
import { api, queryString } from '~/lib/api';
import { queryKeys } from '~/lib/query-client';
import type { Paginated, Quotation } from '~/lib/types';

export interface QuotationListParams {
  page: number;
  pageSize: number;
  search: string;
  status?: QuotationStatus;
  dateFrom?: string;
  dateTo?: string;
}

export function useQuotations(params: QuotationListParams) {
  return useQuery({
    queryKey: queryKeys.quotations(params),
    queryFn: () =>
      api.get<Paginated<Quotation>>(
        `/api/quotations${queryString({
          page: params.page,
          pageSize: params.pageSize,
          search: params.search,
          status: params.status,
          dateFrom: params.dateFrom,
          dateTo: params.dateTo,
        })}`,
      ),
    placeholderData: (previous) => previous,
  });
}

export function useQuotation(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.quotation(id ?? ''),
    queryFn: () => api.get<Quotation>(`/api/quotations/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateQuotation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: QuotationInput) => api.post<Quotation>('/api/quotations', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['quotations'] });
      void queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useUpdateQuotation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: QuotationInput }) =>
      api.put<Quotation>(`/api/quotations/${id}`, input),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['quotations'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.quotation(variables.id) });
    },
  });
}

export function useUpdateQuotationStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: QuotationStatus }) =>
      api.patch<Quotation>(`/api/quotations/${id}/status`, { status }),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['quotations'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.quotation(variables.id) });
    },
  });
}

export function useDeleteQuotation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/quotations/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['quotations'] });
    },
  });
}

/** ออกใบกำกับภาษีจากใบเสนอราคานี้ */
export function useIssueTaxInvoiceFromQuotation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (quotationId: string) =>
      api.post('/api/tax-invoices/from-quotation', { quotationId }),
    onSuccess: (_, quotationId) => {
      void queryClient.invalidateQueries({ queryKey: ['quotations'] });
      void queryClient.invalidateQueries({ queryKey: ['tax-invoices'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.quotation(quotationId) });
    },
  });
}
